<template>
  <section class="card">
    <h2>合作撮合</h2>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <div class="filters filters-panel">
      <div class="flt">
        <input v-model.trim="filters.q" class="flt-input flt-input--wide" placeholder="搜索供需/提交人/字段内容" />
      </div>
      <div class="flt">
        <FancySelect v-model="filters.status" class="flt-select" :options="statusOptions" placeholder="跟进状态" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th class="col-seq">序号</th>
          <th class="col-center">供需标题</th>
          <th>提交人</th>
          <th>单位</th>
          <th>手机</th>
          <th>状态</th>
          <th>提交时间</th>
          <th>操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(item, idx) in pagedFilteredRows" :key="item.id">
          <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
          <td class="col-center cell-title" :title="item.postTitle">{{ item.postTitle || '—' }}</td>
          <td>{{ item.submitterName || '—' }}</td>
          <td>{{ item.submitterCompany || '—' }}</td>
          <td>{{ item.submitterPhone || '—' }}</td>
          <td>
            <span class="pill" :class="statusPillClass(item.contactStatusCode)">
              {{ statusText(item.contactStatusCode) }}
            </span>
          </td>
          <td>{{ formatDateTime(item.createdAt) }}</td>
          <td class="col-ops">
            <button type="button" class="btn btn-edit" @click="openDetail(item)">查看</button>
          </td>
        </tr>
      </tbody>
    </table>
    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="filteredRows.length"
    />
    <p v-if="!filteredRows.length && !errorMsg" class="muted empty-hint">暂无合作意向</p>

    <div v-if="showDetail" class="mask" @click.self="showDetail = false">
      <div class="dialog dialog--shell dialog--detail" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">合作意向详情</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showDetail = false">×</button>
        </div>
        <div class="dialog-body">
          <div v-if="detailItem" class="detail-grid">
            <div class="detail-row"><span class="detail-k">供需标题</span><span class="detail-v">{{ detailItem.postTitle || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">提交人</span><span class="detail-v">{{ detailItem.submitterName || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">单位</span><span class="detail-v">{{ detailItem.submitterCompany || '—' }}</span></div>
            <div class="detail-row"><span class="detail-k">手机</span><span class="detail-v">{{ detailItem.submitterPhone || '—' }}</span></div>
            <div class="detail-row">
              <span class="detail-k">状态</span>
              <span class="detail-v">
                <span class="pill" :class="statusPillClass(detailItem.contactStatusCode)">
                  {{ statusText(detailItem.contactStatusCode) }}
                </span>
              </span>
            </div>
            <div class="detail-row detail-row--block">
              <span class="detail-k">意向内容</span>
              <div class="detail-v detail-fields">
                <div v-for="(f, i) in labeledFields(detailItem)" :key="f.key || i" class="intent-field">
                  <strong>{{ f.label }}：</strong>{{ f.value || '—' }}
                </div>
                <p v-if="!labeledFields(detailItem).length" class="muted intent-empty">—</p>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <button type="button" class="btn btn-cancel" @click="showDetail = false">关闭</button>
          <div v-if="detailItem" class="dialog-foot-actions">
            <button type="button" class="btn btn-delete" @click="setStatus('INVALID')">不合适</button>
            <button type="button" class="btn btn-edit" @click="setStatus('PENDING')">待联系</button>
            <button type="button" class="btn btn-primary" @click="setStatus('CONTACTED')">已联系</button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import { getSupplyIntentsForAdmin, updateSupplyIntentStatusAdmin } from '@/api'

const STATUS_OPTIONS = [
  { value: '', label: '全部状态' },
  { value: 'PENDING', label: '待联系' },
  { value: 'CONTACTED', label: '已联系' },
  { value: 'INVALID', label: '不合适' },
]

function EMPTY_FILTERS() {
  return { q: '', status: '' }
}

export default {
  name: 'SupplyIntentManageView',
  components: { FancySelect, ListPager },
  data() {
    return {
      list: [],
      filters: EMPTY_FILTERS(),
      page: 1,
      pageSize: 10,
      errorMsg: '',
      showDetail: false,
      detailItem: null,
      statusOptions: STATUS_OPTIONS,
    }
  },
  computed: {
    filteredRows() {
      const q = String(this.filters.q || '').trim().toLowerCase()
      const st = String(this.filters.status || '').trim().toUpperCase()
      return (this.list || []).filter((item) => {
        if (st && String(item.contactStatusCode || '').toUpperCase() !== st) return false
        if (!q) return true
        const hay = [
          item.postTitle,
          item.submitterName,
          item.submitterCompany,
          item.submitterPhone,
          ...(Array.isArray(item.labeledFields) ? item.labeledFields.map((f) => `${f.label}${f.value}`) : []),
        ]
          .join(' ')
          .toLowerCase()
        return hay.includes(q)
      })
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    pagedFilteredRows() {
      return slicePageRows(this.filteredRows, this.page, this.pageSize)
    },
  },
  watch: {
    'filteredRows.length'(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
  },
  mounted() {
    this.load()
  },
  methods: {
    async load() {
      try {
        this.list = (await getSupplyIntentsForAdmin()) || []
        this.errorMsg = ''
      } catch (e) {
        this.errorMsg = e.message || '读取失败'
      }
    },
    resetFilters() {
      this.page = 1
      this.filters = EMPTY_FILTERS()
    },
    statusText(code) {
      const c = String(code || '').toUpperCase()
      if (c === 'CONTACTED') return '已联系'
      if (c === 'INVALID') return '不合适'
      return '待联系'
    },
    statusPillClass(code) {
      const c = String(code || '').toUpperCase()
      if (c === 'CONTACTED') return 'pill-green'
      if (c === 'INVALID') return 'pill-red'
      return 'pill-orange'
    },
    formatDateTime(v) {
      if (!v) return '—'
      const s = String(v).replace('T', ' ')
      return s.length >= 19 ? s.slice(0, 19) : s
    },
    labeledFields(item) {
      return item && Array.isArray(item.labeledFields) ? item.labeledFields : []
    },
    openDetail(item) {
      this.detailItem = item || null
      this.showDetail = !!item
    },
    async setStatus(code) {
      if (!this.detailItem || this.detailItem.id == null) return
      try {
        await updateSupplyIntentStatusAdmin(this.detailItem.id, code)
        this.detailItem = { ...this.detailItem, contactStatusCode: code }
        await this.load()
        this.errorMsg = ''
      } catch (e) {
        this.errorMsg = e.message || '更新失败'
      }
    },
  },
}
</script>

<style scoped>
.cell-title {
  max-width: 220px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.intent-empty {
  margin: 0;
  text-align: left;
}
</style>
